import { useEffect, useState } from "react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFixedFooter,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import type { Signal } from "./schemas";

const MAX_SIGNALS = 3;

/**
 * The signal pick: up to three toggle chips, in pick order.
 *
 * Order is meaning here, not presentation — the first pick is the badge
 * signal, so each selected chip is numbered rather than merely highlighted.
 * Chips over a native multi-select for the same reason as the admin agenda's
 * MultiPicker: a native multi-select is unusable with a keyboard and invisible
 * on touch. The cap disables only the unselected chips, so there is always a
 * way back under it.
 *
 * In "prompt" mode (first login, nothing recorded yet) the secondary action is
 * Skip, which SAVES an empty selection — a recorded answer that stops the
 * prompt from nagging. Dismissing the dialog instead records nothing and the
 * prompt returns next visit. In "edit" mode the secondary action is Cancel.
 */
export function SignalPickerDialog({
  open,
  onOpenChange,
  mode,
  signals,
  initialSignalIds,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "prompt" | "edit";
  signals: Signal[];
  initialSignalIds: string[];
  onSave: (signalIds: string[]) => void;
  saving: boolean;
}) {
  const [selected, setSelected] = useState<string[]>(initialSignalIds);

  // Re-arm the draft each time the dialog opens; keeping it in sync while
  // closed would clobber an in-flight edit on unrelated re-renders.
  // Deliberately keyed on `open` alone: initialSignalIds must not reset the
  // draft mid-edit. The seed is filtered to live signals — a stored id whose
  // signal was deleted renders no chip (so it could never be deselected) and
  // fails the rules' exists() check on every save.
  useEffect(() => {
    if (open)
      setSelected(
        initialSignalIds.filter((id) =>
          signals.some((signal) => signal.id === id),
        ),
      );
  }, [open]);

  const atCap = selected.length >= MAX_SIGNALS;
  const toggle = (id: string) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Pick your signals</DialogTitle>
          <DialogDescription>
            Choose up to three topics you want to talk about at this event. Your
            first pick is your badge signal — other attendees see it next to
            your name.
          </DialogDescription>
        </DialogHeader>
        <div
          className="flex flex-wrap gap-2 py-2"
          data-testid="signal-picker-options"
        >
          {signals.map((signal) => {
            const position = selected.indexOf(signal.id);
            const on = position >= 0;
            return (
              <button
                key={signal.id}
                type="button"
                onClick={() => toggle(signal.id)}
                disabled={(!on && atCap) || saving}
                aria-pressed={on}
                data-testid={`signal-option-${signal.id}`}
                className={`flex max-w-full min-w-0 items-center gap-1.5 whitespace-normal wrap-anywhere rounded-lg border px-3 py-1.5 text-left text-sm transition-colors max-sm:min-h-11 ${
                  on
                    ? "border-lit bg-hb-blue-fill text-white"
                    : "border-border text-muted-foreground hover:border-lit disabled:opacity-40"
                }`}
              >
                {on && (
                  <span
                    aria-hidden="true"
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/25 text-2xs font-bold"
                  >
                    {position + 1}
                  </span>
                )}
                {signal.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {selected.length === 0
            ? `Pick up to ${MAX_SIGNALS}.`
            : `${selected.length} of ${MAX_SIGNALS} picked — #1 goes on your badge.`}
        </p>
        {/* Outside the scrolling list, so Save and Skip stay in reach on a
            phone however many signals there are. */}
        <DialogFixedFooter className="border-t border-border px-6 py-4 sm:rounded-b-2xl">
          <DialogFooter className="w-full">
            {mode === "prompt" ? (
              <Button
                variant="outline"
                onClick={() => onSave([])}
                disabled={saving}
                data-testid="signal-picker-skip"
              >
                Skip
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="cta"
              onClick={() => onSave(selected)}
              disabled={saving || (mode === "prompt" && selected.length === 0)}
              data-testid="signal-picker-save"
            >
              {saving ? "Saving..." : "Save signals"}
            </Button>
          </DialogFooter>
        </DialogFixedFooter>
      </DialogContent>
    </Dialog>
  );
}
